import React, { Component } from "react";
import { ThemeProvider } from "styled-components";
import colors from "@streeteasy/design-system/deprecated/colors";
import ArrowIcon from "@streeteasy/design-system/deprecated/Icons/SVG/ArrowIcon";
import XButton from "@streeteasy/design-system/deprecated/XButton";
import { bool, func, node, number, objectOf, oneOf, string } from "prop-types";

import {
  BodyWrapper,
  CloseButtonWrapper,
  ContentWrapper,
  ModalWrapper
} from "./styled";

/**
 * Modal component which sits fixed on top of page with a translucent overlay
 * and renders any children supplied to it. Can be dismissed by clicking a
 * close button or anywhere outside of its content box.
 *
 */
class Modal extends Component {
  static enableBodyScroll() {
    document.body.style.overflow = "auto";
  }

  static disableBodyScroll() {
    document.body.style.overflow = "hidden";
  }

  static propTypes = {
    /**
     * Any nodes that should be rendered inside the modal's content box.
     */
    children: node,
    /**
     * @ignore
     */
    className: string,
    /**
     * Optional custom color for the close button, defaults to goatGrey from
     * the StreetEasy color palette.
     */
    closeButtonColor: string,
    /**
     * Optional value specifying the width of the modal content.
     */
    contentWidth: string,
    /**
     * Whether the modal has back arrow button.
     */
    isBackAvailable: bool,
    /**
     * Whether the modal should be visible or not.
     */
    isOpen: bool,
    /**
     * Function is called when the modal opens.
     */
    handleOpen: func,
    /**
     * Function to call on back arrow button clicked.
     */
    onBackClick: func,
    /**
     * Function to call when the modal should be closed, e.g. when the user
     * clicks the close button or outside of the content box. The parent is
     * responsible for updating the isOpen prop accordingly.
     *
     * @param {Object} params
     * @param {SyntheticEvent} params.event The react `SyntheticEvent`
     */
    onRequestClose: func,
    /**
     * @ignore
     */
    style: objectOf(string),
    /**
     *  String Enum: one of "classicBlueHeader", "default", "lightBox." LightBox-themed Modals maintain distance
     *  between content and viewport on mobile devices, while "default" and "classicBlueHeader"-themed Modals do not.
     */
    theme: oneOf(["classicBlueHeader", "default", "lightBox"]),
    /**
     * Optional value for the top position of the content box,
     * defaults to "120px".
     */
    top: string,
    /**
     * Optional value for the z-index of the modal, defaults to 99999.
     */
    zIndex: number
  };

  static defaultProps = {
    children: null,
    className: null,
    closeButtonColor: colors.goatGrey,
    contentWidth: null,
    isBackAvailable: false,
    isOpen: true,
    handleOpen: () => {},
    onRequestClose: () => {},
    onBackClick: () => {},
    style: null,
    theme: "default",
    top: "120px",
    zIndex: 99999
  };

  componentDidMount() {
    const { isOpen } = this.props;

    if (isOpen) {
      Modal.disableBodyScroll();
      document.addEventListener("keydown", this.handleKeyDown);
    }
  }

  componentDidUpdate(prevProps) {
    const { isOpen } = this.props;
    const { isOpen: wasOpen } = prevProps;

    if (wasOpen && !isOpen) {
      Modal.enableBodyScroll();
      document.removeEventListener("keydown", this.handleKeyDown);
    } else if (!wasOpen && isOpen) {
      const { handleOpen } = this.props;

      Modal.disableBodyScroll();
      document.addEventListener("keydown", this.handleKeyDown);
      handleOpen();
    }
  }

  componentWillUnmount() {
    const { isOpen } = this.props;
    document.removeEventListener("keydown", this.handleKeyDown);
    if (isOpen) {
      Modal.enableBodyScroll();
    }
  }

  handleContentClick = event => {
    event.stopPropagation();
  };

  handleMouseDown = event => {
    this.mouseDownTarget = event.target;
  };

  handleWrapperClick = event => {
    // fixes click and drag closure in Google Chrome
    if (this.mouseDownTarget === event.target) {
      const { onRequestClose } = this.props;
      onRequestClose({ event });
    }
  };

  handleKeyDown = event => {
    const { onBackClick, onRequestClose } = this.props;
    if (event.key === "Escape" || event.key === "Esc" /* for edge / ie */) {
      onRequestClose();
    }
    if (event.key === "Backspace") {
      onBackClick();
    }
  };

  render() {
    const {
      children,
      className,
      closeButtonColor,
      contentWidth,
      isBackAvailable,
      isOpen,
      onBackClick,
      onRequestClose,
      style,
      theme,
      top,
      zIndex,
      ...otherProps
    } = this.props;

    return (
      isOpen && (
        <ThemeProvider
          theme={{
            name: theme
          }}
        >
          <ModalWrapper
            className={className}
            onClick={this.handleWrapperClick}
            onMouseDown={this.handleMouseDown}
            style={style}
            zIndex={zIndex}
            {...otherProps}
          >
            <ContentWrapper
              aria-modal
              contentWidth={contentWidth}
              onClick={this.handleContentClick}
              role="dialog"
              top={top}
            >
              <CloseButtonWrapper isBackAvailable={isBackAvailable}>
                {isBackAvailable && (
                  <div
                    aria-label="back"
                    onClick={onBackClick}
                    onKeyDown={this.handleKeyDown}
                    role="button"
                    style={{ cursor: "pointer" }}
                    tabIndex="0"
                  >
                    <ArrowIcon fillColor="goatGrey" isThinArrow />
                  </div>
                )}
                <XButton color={closeButtonColor} onClick={onRequestClose} />
              </CloseButtonWrapper>
              <BodyWrapper>{children}</BodyWrapper>
            </ContentWrapper>
          </ModalWrapper>
        </ThemeProvider>
      )
    );
  }
}

/**
 * @deprecated please use either:
 *   - @streeteasy/design-system/side-panel
 *   - @streeteasy/design-system/dialog
 *   - @streeteasy/design-system/modal
 */
export default Modal;
